import { useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { springs } from '../../animation';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useAuthStore } from '../../hooks/useAuthStore';
import { useSoundEffects } from '../../hooks/useSoundEffects';
import { apiClient, getErrorMessage } from '../../services/api';
import AnimatedButton from '../../components/ui/AnimatedButton';
import MindoraCharacter from '../../components/character/MindoraCharacter';

const signupSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Must contain an uppercase letter')
    .regex(/[a-z]/, 'Must contain a lowercase letter')
    .regex(/\d/, 'Must contain a number'),
});

type SignupForm = z.infer<typeof signupSchema>;

export default function WelcomeSignupScreen() {
  const navigate = useNavigate();
  const { login, setLoading } = useAuthStore();
  const { play } = useSoundEffects();
  const reduced = useReducedMotion();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignupForm>({
    resolver: zodResolver(signupSchema),
  });

  const onSubmit = async (data: SignupForm) => {
    try {
      setError('');
      setLoading(true);

      await apiClient.post('/auth/register', {
        email: data.email,
        password: data.password,
        role: 'parent',
      });

      const loginResponse = await apiClient.post('/auth/login', {
        email: data.email,
        password: data.password,
      });
      const result = loginResponse.data;

      if (!result.success) {
        throw new Error(result.error?.message || 'Login failed');
      }

      login(result.data.user, result.data.accessToken, result.data.refreshToken);
      play('success');
      navigate('/parent/setup-child');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSocialStub = useCallback(() => {
    play('tap');
    // Visual-only stub — show a gentle "coming soon" message
    setError('Coming soon! For now, sign up with email below.');
    setTimeout(() => setError(''), 3000);
  }, [play]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-6 py-12">
      <motion.div
        className="w-full max-w-sm"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={reduced ? { duration: 0.3 } : springs.gentle}
      >
        <div className="flex justify-center mb-6">
          <MindoraCharacter mood="happy" size="lg" />
        </div>

        <h2 className="font-display text-kid-2xl text-kid-text text-center mb-2">
          Get started in 10 seconds
        </h2>
        <p className="text-kid-text-soft font-kid text-center mb-6">
          Free to try. No credit card needed.
        </p>

        {/* Social sign-in stubs */}
        <div className="space-y-3 mb-4">
          <button
            type="button"
            onClick={handleSocialStub}
            className="w-full flex items-center justify-center gap-3 bg-kid-surface rounded-kid p-3.5 shadow-kid border-2 border-kid-border font-kid font-semibold text-kid-text hover:bg-kid-bg transition-colors touch-target"
          >
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>
          <button
            type="button"
            onClick={handleSocialStub}
            className="w-full flex items-center justify-center gap-3 bg-kid-text rounded-kid p-3.5 shadow-kid font-kid font-semibold text-white hover:opacity-90 transition-opacity touch-target"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            Continue with Apple
          </button>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 h-px bg-kid-border" />
          <span className="text-kid-text-soft font-kid text-kid-sm">or</span>
          <div className="flex-1 h-px bg-kid-border" />
        </div>

        {error && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="mb-4 p-3 bg-kid-dandelion/15 border-2 border-kid-dandelion/30 text-kid-text rounded-kid text-sm font-kid"
          >
            {error}
          </motion.div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div>
            <label className="block font-kid font-semibold text-kid-sm text-kid-text mb-2">
              Email Address
            </label>
            <input
              type="email"
              {...register('email')}
              className="input-kid"
              placeholder="your@email.com"
              autoComplete="email"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-kid-coral font-kid">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label className="block font-kid font-semibold text-kid-sm text-kid-text mb-2">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                {...register('password')}
                className="input-kid pr-12"
                placeholder="8+ characters"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-kid-text-soft hover:text-kid-text p-2 rounded-kid-sm touch-slop"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-sm text-kid-coral font-kid">{errors.password.message}</p>
            )}
          </div>

          <AnimatedButton
            variant="marshmallow"
            className="btn-marshmallow-meadow w-full"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating Account...' : 'Create Account'}
          </AnimatedButton>
        </form>

        <p className="text-center font-kid text-kid-sm text-kid-text-soft mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-kid-ocean font-semibold hover:underline">
            Sign in
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
